import {
  ApolloError,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationTuple,
  TypedDocumentNode,
  useMutation,
} from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import { DocumentNode } from 'graphql'
import { useCallback, useState } from 'react'

import { useCustomerId } from './useCustomerId'

export interface AdditionalOptions {
  noAuth?: boolean
}

type OmitProvided<TVariables> = Omit<TVariables, 'customerId'>

const defaultOptions: AdditionalOptions = { noAuth: false }

const useCamelMutation = <TData = unknown, TVariables = Record<string, unknown>>(
  mutation: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables> & AdditionalOptions,
): [
  MutationTuple<TData, OmitProvided<TVariables>>[0],
  Omit<MutationTuple<TData, OmitProvided<TVariables>>[1], 'error'> & {
    error?: ApolloError | Error
  },
] => {
  const { noAuth, ...hookOptions } = { ...defaultOptions, ...(options || {}) }
  const { getAccessTokenSilently } = useAuth0()
  const customerId = useCustomerId()
  const [tokenError, setTokenError] = useState<Error | null>(null)
  const [doMutation, { error: queryError, ...mutationResultProps }] = useMutation(
    mutation,
    hookOptions as unknown as MutationHookOptions<TData, TVariables>,
  )

  const doCamelMutation = useCallback(
    async (mutationOptions: MutationFunctionOptions<TData, OmitProvided<TVariables>> = {}) => {
      setTokenError(null)
      try {
        let accessToken: string | undefined = undefined
        if (!noAuth) {
          accessToken = await getAccessTokenSilently()
        }
        return await doMutation({
          ...mutationOptions,
          // Although the added `customerId` variable will be returned, there's no need to indicate
          // it's addition to the return type. We cast the variables type to the generic parameter
          // instead to maintain expected return type.
          variables: {
            ...mutationOptions.variables,
            customerId,
          } as unknown as TVariables,
          context: {
            ...mutationOptions.context,
            uri: process.env.GATSBY_CAMEL_URL,
            token: accessToken,
          },
        })
      } catch (accessTokenError) {
        setTokenError(
          accessTokenError instanceof Error ? accessTokenError : new Error(`${accessTokenError}`),
        )
        return {}
      }
    },
    [customerId, doMutation, getAccessTokenSilently, noAuth],
  )

  return [doCamelMutation, { ...mutationResultProps, error: tokenError || queryError }]
}

export default useCamelMutation
